import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import {
  isPremiumUser,
  isSignatureValid,
  validateTemplateAccess,
} from "../utils/signatureValidator";

/**
 * Custom hook for managing quota data with signature validation
 *
 * Features:
 * - Fetches quota from API using React Query (global caching)
 * - Validates signature
 * - Provides user tier information
 * - Handles template access control
 *
 * @param {Object} options - Hook options
 * @param {boolean} options.enabled - Enable/disable query (default: true)
 * @param {number} options.refetchInterval - Auto-refresh interval in ms (default: null)
 * @returns {Object} Quota state and methods
 */
export const useQuota = ({ enabled = true, refetchInterval = null } = {}) => {
  const queryClient = useQueryClient();

  /**
   * Fetches quota data from API
   */
  const fetchQuota = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("No authentication token found");
    }

    const response = await fetch("/api/analysis/quota", {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Quota fetch failed: ${response.status}`);
    }

    const data = await response.json();

    // Validate response structure
    if (!data.signature || !data.timestamp || !data.data) {
      console.warn("[useQuota] Invalid response structure", data);
      throw new Error("Invalid quota response structure");
    }

    console.log("[useQuota] Quota fetched successfully", {
      tier: data.data.tier,
      signature: data.signature.substring(0, 16) + "...",
      timestamp: data.timestamp,
      expiresAt: data.expiresAt,
    });

    return data;
  };

  // Use React Query for global caching
  const {
    data: quotaData,
    isLoading,
    error,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ["quota"],
    queryFn: fetchQuota,
    enabled,
    staleTime: 60 * 1000, // Cache for 1 minute
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes (was cacheTime)
    refetchOnWindowFocus: true,
    refetchInterval,
    retry: 2,
  });

  /**
   * Validates if template access is allowed
   */
  const canAccessTemplate = useCallback(
    (templateIsPremium) => {
      return validateTemplateAccess(quotaData, templateIsPremium);
    },
    [quotaData],
  );

  /**
   * Checks if signature is still valid
   */
  const hasValidSignature = useCallback(() => {
    return isSignatureValid(quotaData);
  }, [quotaData]);

  /**
   * Gets user tier
   */
  const userTier = quotaData?.data?.tier || quotaData?.tier || "free";

  /**
   * Checks if user is premium
   */
  const isPremium = isPremiumUser(quotaData);

  /**
   * Forces a quota refresh (e.g., after upgrade)
   */
  const refreshQuota = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["quota"] });
  }, [queryClient]);

  // Flatten the quota data for easier access
  // Use useMemo to prevent unnecessary re-renders
  const flattenedData = useMemo(() => {
    if (!quotaData?.data) return null;

    const isPro =
      quotaData.data.tier === "premium" ||
      quotaData.data.tier === "pro" ||
      quotaData.data.isUnlimited;

    return {
      ...quotaData.data,
      // Add copilot fields at root level for backward compatibility
      copilotRemaining: quotaData.data.copilot?.remaining,
      copilotTotal: quotaData.data.copilot?.total,
      copilotUsed: quotaData.data.copilot?.used,
      isPro,
    };
  }, [quotaData]);

  /**
   * Checks if user has unlimited copilot access
   */
  const isUnlimitedCopilot = useCallback(() => {
    return flattenedData?.isPro || flattenedData?.isUnlimited || false;
  }, [flattenedData]);

  /**
   * Gets display text for copilot quota
   * Returns infinity symbol for premium, usage count for free
   */
  const getCopilotDisplayText = useCallback(() => {
    if (!flattenedData) return "";

    if (isUnlimitedCopilot()) {
      return "∞ Unlimited";
    }

    return `${flattenedData.copilotRemaining}/${flattenedData.copilotTotal} uses left`;
  }, [flattenedData, isUnlimitedCopilot]);

  /**
   * Checks if user can use copilot (has quota or is premium)
   */
  const canUseCopilot = useCallback(() => {
    if (!flattenedData) return false;
    return isUnlimitedCopilot() || flattenedData.copilotRemaining > 0;
  }, [flattenedData, isUnlimitedCopilot]);

  return {
    // State
    quotaData,
    data: flattenedData, // Flattened data for easy access
    loading: isLoading,
    error: error?.message || null,
    lastFetched: dataUpdatedAt,
    isLoading, // Alias for consistency

    // User info
    userTier,
    isPremium,

    // Validation
    hasValidSignature,
    canAccessTemplate,

    // Helper methods
    isUnlimitedCopilot,
    getCopilotDisplayText,
    canUseCopilot,

    // Actions
    fetchQuota: refreshQuota, // Alias for backward compatibility
    refreshQuota,
  };
};

export default useQuota;
